import React, { useCallback, useMemo, useState } from 'react';
import ChevronDown from 'assets/svg/ChevronDown';
import { formatNumWithKMB } from 'utils/helperMethods';
import type {
    OptimizeResultConfig,
    ResultTableColumnType,
    ResultTableHeader,
} from '../../../../config/optimizeResultConfig';
import type {
    OptimiseResultAttributeRow,
    OptimiseResultItem,
    OptimiseResultRange,
} from '../../../../types/optimiseResult';
import {
    TPO_OPTIMISE_RESULT_TABLE,
    tpoTableCellTestId,
    tpoTableColTestId,
    tpoTableExpandTestId,
    tpoTableRowTestId,
} from '../../../../config/testIds';

const SURFACE = 'optimise_result';
const DEPTH_INDENT_PX = 20;

interface Props {
    results: OptimiseResultItem[];
    config: OptimizeResultConfig;
    /** `constraintLevelOptions` key -> display label, for the level group headers. */
    levelLabels: Record<string, string>;
}

function formatScalar(raw: unknown): string {
    if (raw == null || raw === '') return '-';
    const num = Number(raw);
    return isNaN(num) ? String(raw) : formatNumWithKMB(num);
}

function formatRange(range: OptimiseResultRange | null | undefined): string {
    if (!range) return '-';
    if (range.min === range.max) return formatNumWithKMB(range.min);
    return `${formatNumWithKMB(range.min)} - ${formatNumWithKMB(range.max)}`;
}

const VARIANCE_COLOR: Record<'positive' | 'negative', string> = {
    positive: '#52c41a',
    negative: '#ff4d4f',
};

const TYPE_FLEX: Record<ResultTableColumnType, number> = {
    label: 2,
    range: 1.5,
    string: 1,
    variance: 1,
};

type ResultRowKind = 'kpi' | 'level' | 'attribute';

interface ResultRow {
    key: string;
    kind: ResultRowKind;
    label: string;
    depth: number;
    /** Only attribute rows carry values; the two group tiers are headers. */
    data?: OptimiseResultAttributeRow;
    childKeys: string[];
}

/**
 * Flattens the two-level response into KPI -> level -> attribute rows.
 *
 * v3 groups `level_details` by `attribute_level`, so one constraint can report at
 * several levels at once. That is no longer expressible as a single flat/nested
 * toggle, which is why the old `isTopLevel` flag is gone.
 */
function buildRows(results: OptimiseResultItem[], levelLabels: Record<string, string>): ResultRow[] {
    const rows: ResultRow[] = [];

    for (const item of results) {
        const levelKeys: string[] = [];
        const levelRows: ResultRow[] = [];

        for (const group of item.level_details) {
            const levelKey = `${item.constraint_name}_${group.attribute_level}`;
            levelKeys.push(levelKey);

            const attributeRows: ResultRow[] = group.attribute_selections.map((detail) => ({
                key: `${levelKey}_${detail.attribute_name}`,
                kind: 'attribute' as const,
                label: detail.attribute_name,
                depth: 2,
                data: detail,
                childKeys: [],
            }));

            levelRows.push(
                {
                    key: levelKey,
                    kind: 'level',
                    label: levelLabels[group.attribute_level] ?? group.attribute_level,
                    depth: 1,
                    childKeys: attributeRows.map((row) => row.key),
                },
                ...attributeRows,
            );
        }

        rows.push(
            {
                key: item.constraint_name,
                kind: 'kpi',
                label: item.label,
                depth: 0,
                childKeys: levelKeys,
            },
            ...levelRows,
        );
    }

    return rows;
}

function renderCell(col: ResultTableHeader, row: ResultRow): React.ReactNode {
    if (row.kind !== 'attribute' || !row.data) return null;

    const detail = row.data as unknown as Record<string, unknown>;
    switch (col.type) {
        case 'range':
            return formatRange(detail[col.key] as OptimiseResultRange | null);
        case 'string':
            return formatScalar(detail[col.key]);
        case 'variance': {
            const display = row.data.variance_display ?? formatScalar(row.data.variance);
            return (
                <span style={{ color: VARIANCE_COLOR[row.data.variance_type] }} className="font-medium">
                    {display}
                </span>
            );
        }
        default:
            return null;
    }
}

const OptimiseResultTable: React.FC<Props> = ({ results, config, levelLabels }) => {
    const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(() => new Set());

    const rows = useMemo(() => buildRows(results, levelLabels), [results, levelLabels]);

    const toggleExpand = useCallback((key: string) => {
        setCollapsedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    // A row hides when itself or any ancestor group is collapsed. Tracking collapses
    // rather than expansions keeps the whole tree open by default.
    const hiddenKeys = useMemo(() => {
        const rowByKey = new Map(rows.map((row) => [row.key, row]));
        const hidden = new Set<string>();
        const hide = (key: string) => {
            const row = rowByKey.get(key);
            if (!row) return;
            hidden.add(key);
            row.childKeys.forEach(hide);
        };
        for (const key of collapsedKeys) rowByKey.get(key)?.childKeys.forEach(hide);
        return hidden;
    }, [rows, collapsedKeys]);

    return (
        <div className="flex flex-col" data-testid={TPO_OPTIMISE_RESULT_TABLE}>
            <div className="flex bg-[#fafafa] border border-[#f0f0f0] rounded-t-md sticky top-0 z-10">
                {config.resultTableHeaders.map((col) => (
                    <div
                        key={col.key}
                        style={{ flex: TYPE_FLEX[col.type] }}
                        className="px-4 py-3 text-sm font-semibold text-[#18181b]"
                        data-testid={tpoTableColTestId(SURFACE, col.key)}
                    >
                        {col.label}
                    </div>
                ))}
            </div>

            <div className="border border-t-0 border-[#f0f0f0] rounded-b-md">
                {rows.map((row) => {
                    if (hiddenKeys.has(row.key)) return null;
                    const isExpandable = row.childKeys.length > 0;
                    const isExpanded = isExpandable && !collapsedKeys.has(row.key);

                    return (
                        <div
                            key={row.key}
                            className={`flex items-center border-b border-[#f0f0f0] last:border-none transition-colors hover:bg-[#fafafa] ${
                                row.kind === 'attribute' ? 'text-[#595959]' : 'text-[#18181b]'
                            }`}
                            data-testid={tpoTableRowTestId(SURFACE, row.key)}
                            data-row-kind={row.kind}
                            data-state-expanded={isExpanded ? 'true' : undefined}
                        >
                            {config.resultTableHeaders.map((col) => (
                                <div
                                    key={col.key}
                                    style={{
                                        flex: TYPE_FLEX[col.type],
                                        paddingLeft: col.type === 'label' ? 16 + row.depth * DEPTH_INDENT_PX : 16,
                                    }}
                                    className="pr-4 py-3.5 text-sm"
                                    data-testid={tpoTableCellTestId(SURFACE, row.key, col.key)}
                                >
                                    {col.type === 'label' ? (
                                        <div className="flex items-center gap-1.5">
                                            {isExpandable ? (
                                                <span
                                                    className="cursor-pointer flex items-center p-1 text-[#595959] hover:text-[#44338a] transition-colors"
                                                    onClick={() => toggleExpand(row.key)}
                                                    data-testid={tpoTableExpandTestId(SURFACE, row.key)}
                                                >
                                                    <ChevronDown
                                                        className={isExpanded ? 'rotate-180' : 'rotate-0'}
                                                        size={12}
                                                    />
                                                </span>
                                            ) : (
                                                // Keeps leaf labels aligned with the ones that have a chevron.
                                                <span className="w-[20px] shrink-0" />
                                            )}
                                            <span>{row.label}</span>
                                        </div>
                                    ) : (
                                        renderCell(col, row)
                                    )}
                                </div>
                            ))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

OptimiseResultTable.displayName = 'OptimiseResultTable';
export default React.memo(OptimiseResultTable);
